import { highlightRoute, queryOnMapMove, unhighlightRoute } from './actions'
import { scrollToHighlight } from './helpers'
import {
    DiscoverRoute, ClusteredRoutes, FullscreenView, Geolocation, MapStyleSelection, SearchOnMapMove,
    ViewportControls, Viewport,
} from '@bikemap/js/map/features'
import Map from '@bikemap/js/map/Map'
import { connectViewport } from '@bikemap/js/integrations/map/connectors'

const MAP_CONTAINER_ID = 'map'

/**
 * Map for the route search.
 */
class SearchMap {

    /** @type {import("redux").Store} */
    _store

    /** @type {Map} */
    _map

    /** @type {ClusteredRoutes} */
    _clusteredRoutes

    /** @type {SearchOnMapMove} */
    _searchOnMapMove

    /** @type {Route} */
    _route = null

    /** @type {number[]} Route IDs */
    _results

    /** @type {number} Route ID */
    _highlight

    /**
     * @param {import("redux").Store} store
     */
    constructor(store) {
        this._store = store

        this._composeMap()
        this._bindToState()
        this._setupDispatch()
    }

    /**
     * Initialize the composition of map and features for this use case.
     */
    _composeMap() {
        const { mapViewport } = this._store.getState()

        const options = {
            viewport: mapViewport,
        }

        this._clusteredRoutes = new ClusteredRoutes
        this._updateRoutes()

        this._searchOnMapMove = new SearchOnMapMove

        this._map = new Map(MAP_CONTAINER_ID, options)
            .addFeature(new MapStyleSelection())
            .addFeature(new FullscreenView)
            .addFeature(new ViewportControls)
            .addFeature(new Geolocation)
            .addFeature(connectViewport(new Viewport, this._store))
            .addFeature(this._clusteredRoutes)
            .addFeature(this._searchOnMapMove)
    }

    /**
     * Make the map reflect the current global state.
     */
    _bindToState() {
        this._store.subscribe(this._updateRoutes.bind(this))
        this._store.subscribe(this._updateHighlight.bind(this))
    }

    /**
     * Make the map dispatch actions on certain events.
     */
    _setupDispatch() {
        this._clusteredRoutes.addEventListener('mouseenterroute', this._handleRouteMouseEnter.bind(this))
        this._clusteredRoutes.addEventListener('clickroute', this._handleRouteClick.bind(this))
        this._searchOnMapMove.addEventListener('mapmovesearch', this._handleMapMoveSearch.bind(this))
    }

    /**
     * When mouse enters a route marker.
     * @param {{ routeId: number }} e
     */
    _handleRouteMouseEnter(e) {
        const { isMapView } = this._store.getState()
        if (!isMapView) {
            this._store.dispatch(highlightRoute(e.routeId))
            scrollToHighlight()
        }
    }

    /**
     * When mouse leaves a route marker.
     */
    _handleRouteMouseLeave() {
        const { isMapView } = this._store.getState()
        if (!this._highlight && !isMapView) {
            this._store.dispatch(unhighlightRoute())
        }
    }

    /**
     * When mouse leaves the marker of a highlighted route.
     */
    _handleHighlightRouteMouseLeave() {
        const { isMapView } = this._store.getState()
        if (!isMapView) {
            this._store.dispatch(unhighlightRoute())
        }
    }

    /**
     * When a route marker has been clicked.
     * @param {{ routeId: number }} e
     */
    _handleRouteClick(e) {
        const { isMapView } = this._store.getState()
        if (isMapView) {
            this._store.dispatch(highlightRoute(e.routeId))
            e.preventDefault()
        }
    }

    /**
     * When search on map move is checked and map viewport changed.
     */
    _handleMapMoveSearch() {
        this._store.dispatch(queryOnMapMove())
    }

    /**
     * Update routes on the map when search results changed.
     */
    _updateRoutes() {
        const { results, routes } = this._store.getState()

        if (results.items !== this._results) {
            this._results = results.items
            if (!results.isQuerying) {
                if (this._highlight) {
                    // Opportunity to remove highlighted route first as there may be no way
                    // to remove it once the marker is gone
                    this._store.dispatch(unhighlightRoute())
                }
                this._clusteredRoutes.setRoutes(results.items.map(routeId => routes[routeId]))
            }
        }
    }

    /**
     * Update the route shown as a whole on the map when highlight changed in state.
     */
    _updateHighlight() {
        const { routes, highlight } = this._store.getState()

        if (
            // Highlight in global state changed / differs from map state
            highlight !== this._highlight &&
            // and is either no highlight or a route we can already show
            (!highlight || (routes[highlight] && routes[highlight].points))
        ) {
            if (this._highlight) {
                this._removeHighlightRoute()
            }

            if (highlight && routes[highlight]) {
                this._showHighlightRoute(routes[highlight])
            }

            this._highlight = highlight
        }
    }

    /**
     * Remove the highlight route shown as a whole.
     */
    _removeHighlightRoute() {
        this._route.remove()
        this._route = null

        this._clusteredRoutes.fadeIn()
    }

    /**
     * Set one route to show as a whole while fading the other markers.
     * @param {import("../../entities").Route} route
     */
    _showHighlightRoute(route) {
        this._route = new DiscoverRoute(route)
        this._route.addEventListener('mouseleave', this._handleHighlightRouteMouseLeave.bind(this))
        this._map.addFeature(this._route)

        this._clusteredRoutes.fadeOut()
    }

}

/** @type {SearchMap} */
let map

/**
 * Initialize the search map and set up state bindings.
 * @param {import("redux").Store} store
 */
export default function initSearchMap(store) {
    try {
        map = map || new SearchMap(store)
    } catch (error) {
        // Continue without map
        console.error(error.message)
        document.getElementById('main').classList.add('map-hidden')
    }
}
